import { type ReactNode, useEffect } from "react";
import { useLocation, Navigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { usePermissions } from "@/contexts/permissions-context";
import { Box, CircularProgress, Typography } from "@mui/material";

interface ProtectedRouteProps {
  children: ReactNode;
}

function ProtectedRoute({ children }: ProtectedRouteProps) {
  const auth = useAuth();
  const location = useLocation();
  const { canAccess, loading: permissionsLoading } = usePermissions();

  useEffect(() => {
    if (!auth.isAuthenticated && !auth.isLoading && !auth.activeNavigator) {
      // Guardar la ruta actual para redirigir después del login
      const returnUrl = location.pathname + location.search;
      auth.signinRedirect({
        state: { returnUrl },
      });
    }
  }, [
    auth.isAuthenticated,
    auth.isLoading,
    auth.activeNavigator,
    auth,
    location,
  ]);

  if (auth.isLoading) {
    return <div>Verificando autenticación...</div>;
  }

  if (auth.error) {
    return (
      <div>
        <p>Error de autenticación: {auth.error.message}</p>
        <button onClick={() => auth.signinRedirect()}>
          Intentar nuevamente
        </button>
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    return <div>Redirigiendo al inicio de sesión...</div>;
  }

  // Esperar a que los permisos se carguen antes de verificar acceso
  if (permissionsLoading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", minHeight: "50vh", gap: 2 }}>
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary">Cargando permisos...</Typography>
      </Box>
    );
  }

  // Verificar si el usuario tiene acceso a esta ruta
  if (!canAccess(location.pathname)) {
    return <Navigate to="/access-denied" replace />;
  }

  return <>{children}</>;
}

export default ProtectedRoute;
